import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { format } from "date-fns";
import { Button } from "@mui/material";
import PrintIcon from "@mui/icons-material/Print";
import { apiClient } from "../../../shared/api/AllApi";
import velanLogo from "../../../shared/assets/velan-plastic-logo.jpg";
import { fetchSystemConfig, readCachedSystemConfig } from "../../../shared/lib/systemConfig";
import { useTranslation } from "react-i18next";

const OwedReceipt = () => {
  const { t } = useTranslation();
  const { id, paymentId } = useParams();
  const [receiptData, setReceiptData] = useState(null);
  const [systemSettings, setSystemSettings] = useState(() => readCachedSystemConfig());

  useEffect(() => {
    let cancelled = false;
    fetchSystemConfig()
      .then((config) => {
        if (!cancelled) setSystemSettings(config);
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, []);

  const cfg = systemSettings ?? readCachedSystemConfig() ?? {};
  const mainCode = cfg.currency_code || "IQD";
  const secondaryCode = cfg.secondary_currency_code || "";

  const companyTitle =
    (cfg.invoice_company_name || "").trim() ||
    (cfg.site_name || "").trim() ||
    t("velanFactoryName");
  const tagline = (cfg.invoice_tagline || "").trim();
  const headerLogoSrc = cfg.invoice_logo_url || cfg.sidebar_logo_url || velanLogo;
  const displayEmail = (cfg.invoice_email || "").trim() || "info@velan-plastic.com";
  const displayWeb = (cfg.invoice_website || "").trim() || "http://velan-plastic.com";
  const phoneLines = (cfg.invoice_phones || "").trim()
    ? cfg.invoice_phones.split(/\r?\n/).map((s) => s.trim()).filter(Boolean)
    : ["0750 456 7566", "0770 234 1022"];
  const addressText = (cfg.invoice_address || "").trim() || t("factoryAddress");
  const footerNote = (cfg.invoice_footer_note || "").trim();

  useEffect(() => {
    // Fetch the payment receipt data using customer id and payment id
    const fetchPaymentReceipt = async () => {
      try {
        const res = await apiClient.get(
          `/customers/${id}/payment-receipt/${paymentId}`
        );
        const data = res.data;
        setReceiptData({
          customerName: data.customer_name,
          oldOwed: data.old_owed,
          payment: data.payment,
          amountMain: data.amount_main,
          amountSecondary: data.amount_secondary,
          exchangeRate: data.exchange_rate,
          totalOwed: data.total_remaind,
          sender: data.sender,
          receiver: data.receiver,
          date: data.created_at,
        });
      } catch (error) {
        setReceiptData(null);
        console.error("Error fetching payment receipt:", error);
      }
    };
    if (id && paymentId) fetchPaymentReceipt();
  }, [id, paymentId]);

  const getBase64Logo = (logoSrc) => {
    return new Promise((resolve, reject) => {
      const img = new Image();
      img.crossOrigin = "anonymous";
      img.onload = () => {
        try {
          const canvas = document.createElement("canvas");
          canvas.width = img.naturalWidth;
          canvas.height = img.naturalHeight;
          const ctx = canvas.getContext("2d");
          ctx.drawImage(img, 0, 0);
          resolve(canvas.toDataURL("image/png"));
        } catch (e) {
          reject(e);
        }
      };
      img.onerror = (err) => {
        console.error("Image load failed:", err);
        reject(err);
      };
      img.src = logoSrc;
    });
  };

  const handlePrint = async () => {
    const printContent = document.querySelector(".printable-content");

    try {
      let logoForPrint = headerLogoSrc;
      try {
        logoForPrint = await getBase64Logo(headerLogoSrc);
      } catch {
        logoForPrint = headerLogoSrc;
      }

      const clonedContent = printContent.cloneNode(true);

      const img = clonedContent.querySelector("img.receipt-header-logo");
      if (img) {
        img.setAttribute("src", logoForPrint);
      }

      const printWindow = window.open("", "_blank");

      printWindow.document.write(`
        <html dir="rtl" lang="ar">
          <head>
            <title>Payment Receipt</title>
            <meta charset="UTF-8" />
            <style>
              body {
                font-family: 'Cairo', Arial, sans-serif;
                direction: rtl;
                background: #fff;
                color: #000;
              }
              img {
                max-width: 100%;
                height: auto;
              }
              table {
                width: 100%;
                border-collapse: collapse;
              }
              th, td {
                border: 1px solid #ddd;
                text-align: right;
              }
              .printable-content {
                width: 100%;
                max-width: 19cm;
                margin: auto;
                box-sizing: border-box;
              }
            </style>
          </head>
          <body>
            <div class="printable-content">
              ${clonedContent.innerHTML}
            </div>
          </body>
        </html>
      `);

      printWindow.document.close();
      printWindow.focus();

      // Ensure the image loads before printing
      printWindow.onload = () => {
        printWindow.print();
        printWindow.close();
        window.location.reload();
      };
    } catch (err) {
      console.error("Error during print setup:", err);
    }
  };

  return (
    <>
      {/* Loading and error states */}
      {!receiptData && (
        <div
          style={{
            textAlign: "center",
            color: "red",
            fontWeight: "bold",
            margin: "2rem",
          }}
        >
          {t("receiptDataNotFound")}
        </div>
      )}
      {receiptData && (
        <div>
          <div
            className="printable-content"
            style={{
              fontFamily: "Cairo, Arial, sans-serif",
              direction: "rtl",
              width: "100%",
              maxWidth: "19cm",
              minHeight: "27cm",
              margin: "1.5cm auto",
              padding: "1cm",
              background: "#fff",
              border: "1px solid #ccc",
              borderRadius: "8px",
              position: "relative",
              boxSizing: "border-box",
            }}
          >
            {/* Receipt Header */}
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                marginBottom: "2.5rem",
                borderBottom: "2px solid #222",
                paddingBottom: "1.2rem",
                zIndex: 2,
                position: "relative",
              }}
            >
              <div
                style={{
                  color: "#111",
                  fontWeight: "bold",
                  fontSize: "2.4rem",
                  letterSpacing: "2px",
                  textShadow: "0 2px 8px rgba(0,0,0,0.10)",
                  fontFamily: "Cairo, Arial, sans-serif",
                }}
              >
                {companyTitle}
                <div
                  style={{
                    fontSize: "1.1rem",
                    color: "#444",
                    fontWeight: 400,
                    marginTop: 4,
                  }}
                >
                  {tagline || null}
                </div>
              </div>
              <div style={{ textAlign: "right" }}>
                <img
                  className="receipt-header-logo"
                  src={headerLogoSrc}
                  alt="Logo"
                  style={{
                    width: "180px", // Make logo bigger
                    height: "auto",
                    background: "#fff",
                  }}
                />
              </div>
            </div>
            {/* Receipt Number (moved below header) */}
            <div
              style={{
                textAlign: "center",
                fontSize: "1.2rem",
                color: "#222",
                fontWeight: 600,
                margin: "0 0 1rem 0", // Adjusted margin to move it higher
                letterSpacing: "1px",
              }}
            >
              {t("receiptNumber")}:{" "}
              <span style={{ color: "#111", fontWeight: 700 }}>
                {paymentId}
              </span>
            </div>
            {/* Receipt Title */}
            <h3
              style={{
                textAlign: "center",
                fontSize: "2rem", // Slightly reduced font size
                color: "#111",
                marginBottom: "2rem", // Adjusted margin to move it higher
                fontWeight: "bold",
                letterSpacing: "1px",
                background: "#f7f7f7",
                borderRadius: "8px",
                border: "1.5px solid #bbb",
                boxShadow: "0 1px 4px rgba(0,0,0,0.08)",
                width: "60%", // Slightly reduced width
                margin: "0 auto 2rem auto", // Adjusted margin
                zIndex: 2,
                position: "relative",
              }}
            >
              {t("paymentReceiptTitle")}
            </h3>
            {/* Receipt Body */}
            <div
              style={{
                fontSize: "1rem", // Reduced font size
                lineHeight: "1.8", // Adjusted line height
                padding: "1rem 1rem 1rem 1rem", // Reduced padding
                marginBottom: "2rem", // Adjusted margin
                background: "#fff",
                borderRadius: "12px", // Reduced border radius
                boxShadow: "0 1px 10px rgba(0,0,0,0.08)", // Reduced shadow
                border: "1px solid #ddd", // Adjusted border thickness
                zIndex: 2,
                position: "relative",
              }}
            >
              <div
                style={{
                  display: "grid",
                  gridTemplateColumns: "190px 1fr",
                  gap: "1.3rem 2.7rem",
                  alignItems: "center",
                }}
              >
                <span style={{ color: "#111", fontWeight: "bold" }}>
                  {t("customerNameLabel")}:
                </span>
                <span style={{ color: "#222", fontWeight: "bold" }}>
                  {receiptData?.customerName}
                </span>
                <span style={{ color: "#111", fontWeight: "bold" }}>
                  {t("paymentDateTimeLabel")}:
                </span>
                <span style={{ color: "#222", fontWeight: "bold" }}>
                  {receiptData?.date
                    ? format(new Date(receiptData.date), "dd/MM/yyyy HH:mm")
                    : ""}
                </span>
                <span style={{ color: "#111", fontWeight: "bold" }}>
                  {t("previousDebtLabel")}:
                </span>
                <span style={{ color: "#222", fontWeight: "bold" }}>
                  {Number(receiptData?.oldOwed).toLocaleString()}{" "}
                  <span style={{ color: "#111", fontWeight: 500 }}>{mainCode}</span>
                </span>
                <span style={{ color: "#111", fontWeight: "bold" }}>
                  {t("paidAmountLabel")}:
                </span>
                <span style={{ color: "#222", fontWeight: "bold" }}>
                  {Number(receiptData?.payment).toLocaleString()}{" "}
                  <span style={{ color: "#111", fontWeight: 500 }}>{mainCode}</span>
                </span>
                {receiptData?.amountMain != null && (
                  <>
                    <span style={{ color: "#111", fontWeight: "bold" }}>{t("mainCurrencyPaidLabel")}:</span>
                    <span style={{ color: "#222", fontWeight: "bold" }}>
                      {Number(receiptData.amountMain).toLocaleString()} {mainCode}
                    </span>
                  </>
                )}
                {receiptData?.amountSecondary != null && Number(receiptData.amountSecondary) > 0 && secondaryCode && (
                  <>
                    <span style={{ color: "#111", fontWeight: "bold" }}>{t("secondaryCurrencyPaidLabel")}:</span>
                    <span style={{ color: "#222", fontWeight: "bold" }}>
                      {Number(receiptData.amountSecondary).toLocaleString()} {secondaryCode}
                      {receiptData?.exchangeRate?.main_per_secondary
                        ? ` (${t("rateLabel")}: 1 ${mainCode} = ${receiptData.exchangeRate.main_per_secondary} ${secondaryCode})`
                        : ""}
                    </span>
                  </>
                )}
                <span style={{ color: "#111", fontWeight: "bold" }}>
                  {t("remainingDebtLabel")}:
                </span>
                <span style={{ color: "#222", fontWeight: "bold" }}>
                  {Number(receiptData?.totalOwed).toLocaleString()}{" "}
                  <span style={{ color: "#111", fontWeight: 500 }}>{mainCode}</span>
                </span>
              </div>
              <div
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  marginTop: "2.5rem",
                  paddingTop: "1.2rem",
                  borderTop: "1px dashed #bbb",
                }}
              >
                <div>
                  <span
                    style={{
                      color: "#111",
                      fontWeight: "bold",
                      marginLeft: "8px",
                    }}
                  >
                    {t("receiverLabel")} :
                  </span>
                  <span style={{ color: "#222", fontWeight: "bold" }}>
                    {receiptData?.receiver}
                  </span>
                </div>
                <div>
                  <span
                    style={{
                      color: "#111",
                      fontWeight: "bold",
                      marginLeft: "8px",
                    }}
                  >
                    {t("senderLabel")} :
                  </span>
                  <span style={{ color: "#222", fontWeight: "bold" }}>
                    {receiptData?.sender}
                  </span>
                </div>
              </div>
            </div>
            {/* Signature Areas */}
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "flex-end",
                margin: "2.5rem 0 1.5rem 0",
                zIndex: 2,
                position: "relative",
              }}
            >
              <div style={{ textAlign: "center", width: "40%" }}>
                <div
                  style={{
                    borderBottom: "2px dotted #111",
                    height: 40,
                    marginBottom: 8,
                  }}
                ></div>
                <span style={{ color: "#111", fontWeight: 500 }}>
                  {t("receiverSignature")}
                </span>
              </div>
              <div style={{ textAlign: "center", width: "40%" }}>
                <div
                  style={{
                    borderBottom: "2px dotted #111",
                    height: 40,
                    marginBottom: 8,
                  }}
                ></div>
                <span style={{ color: "#111", fontWeight: 500 }}>
                  {t("senderSignature")}
                </span>
              </div>
            </div>
            {footerNote ? (
              <div
                style={{
                  textAlign: "center",
                  padding: "10px 12px 0",
                  fontSize: "0.95rem",
                  color: "#333",
                  whiteSpace: "pre-wrap",
                  position: "relative",
                  zIndex: 2,
                }}
              >
                {footerNote}
              </div>
            ) : null}
            {/* Footer Info */}
            <div
              style={{
                position: "absolute",
                bottom: "15px",
                left: 1,
                right: 1,
                background: "#f7f7f7",
                borderRadius: "10px 10px 0 0",
                padding: "1.5rem 2rem 1.2rem 2rem",
                boxShadow: "0 -2px 10px rgba(0,0,0,0.06)",
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                fontSize: "1.05rem",
                zIndex: 2,
              }}
            >
              {/* RIGHT: Location */}
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: "6px",
                  textAlign: "right",
                }}
              >
                <span style={{ color: "#222", fontSize: 14, whiteSpace: "pre-wrap" }}>
                  {addressText}{" "}
                </span>
              </div>

              {/* MIDDLE: Phone numbers */}
              <div
                style={{
                  display: "flex",
                  flexDirection: "column",
                  gap: "4px",
                  alignItems: "center",
                }}
              >
                {phoneLines.map((line, idx) => (
                  <div
                    key={idx}
                    style={{ display: "flex", alignItems: "center", gap: "6px" }}
                  >
                    <span style={{ color: "#222", fontSize: 14 }}>{line}</span>
                  </div>
                ))}
              </div>

              {/* LEFT: Email + Website */}
              <div
                style={{
                  display: "flex",
                  flexDirection: "column",
                  gap: "4px",
                  alignItems: "flex-start",
                }}
              >
                <div
                  style={{ display: "flex", alignItems: "center", gap: "6px" }}
                >
                  <span style={{ color: "#222", fontSize: 14 }}>
                    Email: {displayEmail}
                  </span>
                </div>
                <div
                  style={{ display: "flex", alignItems: "left", gap: "6px" }}
                >
                  <span style={{ color: "#222", fontSize: 14 }}>
                    Web:{displayWeb}
                  </span>
                </div>
              </div>
            </div>
          </div>
          <div
            style={{
              position: "fixed",
              bottom: "40px",
              right: "40px",
              zIndex: 1000,
            }}
            className="no-print"
          >
            <Button
              variant="contained"
              color="primary"
              startIcon={<PrintIcon />}
              onClick={handlePrint}
              style={{ marginBottom: "10px" }}
            >
              {t("Print")}
            </Button>
          </div>
          <style>
            {`
              @media print {
                @page {
                  size: A4;
                  margin: 0;
                }
                body {
                  margin: 0;
                  padding: 0;
                  background: #f4f8fb; // Match on-screen background
                  direction: rtl; // Ensure right-to-left layout
                  font-family: "Cairo, Arial, sans-serif"; // Match on-screen font
                }
                .printable-content {
                  width: 19cm; // Match on-screen width
                  min-height: 27.7cm; // Match on-screen height
                  padding: 2cm 1.5cm 2.5cm 1.5cm; // Match on-screen padding
                  background: #f4f8fb; // Match on-screen background
                  box-shadow: none; // Remove shadow for printing
                  border-radius: 0; // Remove border radius for printing
                  border: 1px solid #3498db; // Match on-screen border
                }
                .no-print {
                  display: none !important;
                }
                .MuiButton-root {
                  display: none !important;
                }
              }
            `}
          </style>
        </div>
      )}
    </>
  );
};

export default OwedReceipt;

